import axios from 'axios'

import {Message, MessageBox} from 'element-ui'
import i18n from '@/lang'
import store from '@/store'
// import {getToken} from '@/utils/auth'

import {codeMap} from "@/config/constants"
// 创建axios实例
const service = axios.create({
    baseURL: "/", // api的base_url
    timeout: 10000 // request timeout
})

/**
 * 按 HTTP 状态码给出用户看得懂的说明。
 *
 * axios 自带的「Request failed with status code 403」是给开发看的，
 * 直接弹给用户等于什么都没说（用户既不知道哪里错了，也不知道该做什么）。
 * 后端的业务 message 优先；后端没带 message 时才落到这里。
 */
function friendlyHttpMessage(status) {
    const key = {
        400: 'badRequest',
        403: 'forbidden',
        404: 'notFound',
        408: 'timeout',
        413: 'payloadTooLarge',
        429: 'tooManyRequests',
        500: 'serverError',
        502: 'serviceUnavailable',
        503: 'serviceUnavailable',
        504: 'serviceUnavailable'
    }[status];
    if (key) return i18n.t('request.' + key);
    return status
        ? i18n.t('request.failedWithStatus', {status})
        : i18n.t('request.networkError');
}

/**
 * 判断一段文案是不是「纯技术性」的，技术性的才替换成人话。
 * - axios 的 Request failed with status code xxx / Network Error / timeout of xxxms exceeded
 */
function isTechnicalMessage(text) {
    const s = String(text == null ? '' : text);
    if (!s) return true;
    return /Request failed with status code/i.test(s)
        || /^Network Error$/i.test(s)
        || /^timeout of \d+ms exceeded$/i.test(s)
        // Spring 的框架级英文消息：出现在"请求打错了方法/路径"时。
        // 用户看不懂，但必须保留可定位信息 —— 见下面附加 method + url 的那段。
        || /Request method '.*' not supported/i.test(s)
        || /Method Not Allowed/i.test(s);
}

// 请求拦截器
service.interceptors.request.use(config => {
    // ⚠ 兜底拦截：空 id 的"题目详情"请求。
    //
    // URL 拼成 /survey/question/ 时，Spring 会把尾斜杠规整成 /survey/question，
    // 而那个路径上挂的是 @PostMapping（新增）和 @PutMapping（保存），
    // 于是这个 GET 得到 **405 "Request method 'GET' not supported"**。
    // 它在浏览器里只表现为一句英文提示、看不出是哪个接口，极难排查。
    //
    // api 层（getQuestion）已经加了守卫，但那个模块被几十处 import，
    // dev server 的 HMR 对它的更新不一定生效；这里再拦一次，
    // 保证**任何调用方**拼出这种 URL 都发不出去。
    if (config.method === 'get' && /\/survey\/question\/?(\?|$)/.test(config.url || '')) {
        console.error('[http] 已拦截空 id 的题目请求：' + config.url
            + ' —— 调用方没有传 questionId')
        return Promise.reject(new Error('题目 id 为空，请求已被拦截'))
    }
    // if (store.getters.token) {
    let token = localStorage.getItem("token");
    if(token && !config.url.endsWith("/login")) {
        config.headers['Authorization'] = token
    }
    // 优先用localStorage里的语言，兜底用Vuex
    let lang = localStorage.getItem("lang") || (store && store.state && store.state.app && store.state.app.language) || "zh";
    const headerLang = lang === 'zh_TW' ? 'zh-TW' : lang;
    config.headers['Accept-Language'] = headerLang;
    return config
}, error => {
    console.log(error)
    Promise.reject(error)
})

// 响应拦截器
service.interceptors.response.use(res => {
    // 对于文件下载（blob），直接返回原始响应，以便上层获取 headers 和二进制数据
    if (res && res.config && res.config.responseType === 'blob') {
        return res;
    }
    let res_data = res.data
    //console.log("res_data", res_data)
    // 只拦截特定的错误状态码
    if (res_data && (res_data.code === 401 || res_data.code === 403)) {
        const bizMsg = res_data.message || res_data.msg || "认证失败，请重新登录!";
        Message({
            message: bizMsg,
            type: 'error',
        });
        // 与 HTTP 层错误保持同一套字段，组件里读 error.message 就能拿到可读文案
        const bizError = new Error(bizMsg);
        bizError.statusCode = res_data.code;
        bizError.friendlyMessage = bizMsg;
        return Promise.reject(bizError);
    }
    return res_data
}, async error => {
    // 处理401错误
    if (error.response && error.response.status === 401) {
        // 区分"被踢下线"（同账号在别处登录）与普通登录过期：
        // 网关对两者都返回 HTTP 401，被踢时 body 带 error === 'KickedOut'（见 ys-gateway-server 的 JwtCheckFilter）。
        const respData = error.response.data;
        const kickedOut = !!(respData && respData.error === 'KickedOut');
        // 清除所有本地存储
        localStorage.clear();
        // 显示提示信息：被踢时给出原因（否则用户只看到"登录已过期"，不知道是账号在别处登录）
        Message({
            message: kickedOut
                ? i18n.t('request.kickedOut')
                : "登录已过期，请重新登录",
            type: 'warning',
            duration: kickedOut ? 3000 : 1000,
            onClose: () => {
                // 提示消息结束后跳转到登录页并整页刷新。
                // 1. 必须带 hash（/#/login）——本项目是 hash 路由，
                //    直接跳 /login 会被 devServer 的兜底代理转发到后端，导致 404 白页。
                // 2. 整页刷新是为了彻底清掉内存中的登录态残留（dev 环境的错误浮层也不会滞留）。
                // 3. 被踢时带 kicked=1：登录页展示常驻提醒（转瞬即逝的提示容易被漏看）。
                window.location.replace(kickedOut ? '/#/login?kicked=1' : '/#/login');
                window.location.reload();
            }
        });
    } else {
        const resp = error.response || {};
        const url = (error && error.config && error.config.url) || '';
        let rawMsg = (resp.data && resp.data.message) || (resp.data && resp.data.msg) || error.message || "请求错误";

        // 特殊处理 Blob 类型的错误响应 (通常在 responseType: 'blob' 且后端返回 JSON 错误时出现)
        if (resp.data instanceof Blob && (resp.data.type === 'application/json' || resp.status === 403 || resp.status === 400)) {
            try {
                const text = await resp.data.text();
                const obj = JSON.parse(text);
                if (obj && (obj.msg || obj.message)) {
                    rawMsg = obj.msg || obj.message;
                }
            } catch (e) {
                // 如果不是 JSON，尝试直接解析为文本 (部分后端可能直接返回文本)
                try {
                    const text = await resp.data.text();
                    if (text && text.length < 100) rawMsg = text;
                } catch (e2) {}
            }
        }

        let showMsg = rawMsg;
        const isExcelImport = /\/survey\/projects\/excelToJson/i.test(url) || /excelToJson-Pro/i.test(url);
        const isDup = /重复题号/.test(rawMsg) || /duplicate\s+question/i.test(rawMsg);
        if ((resp.status === 500 && isExcelImport) || isDup) {
            const lines = String(rawMsg).split(/\n+/).map(s => s.trim()).filter(Boolean);
            const items = [];
            for (let i = 0; i < lines.length; i++) {
                const l = lines[i];
                let m = l.match(/^题号\s+([A-Za-z0-9_-]+)\s+重复[^\[]*\[(.+?)\]/);
                if (!m) m = l.match(/^Question\s+([A-Za-z0-9_-]+).*?rows?[^\[]*\[(.+?)\]/i);
                if (m) items.push(`${m[1]} -> [${m[2]}]`);
            }
            const details = items.length ? items.join('; ') : String(rawMsg).replace(/Excel Pro 解析失败[:：]?\s*/, '');
            showMsg = i18n.t('questionEdit.projectFileView.duplicateQuestionNos', {details});
        }
        
        // 技术性文案一律换成人话：后端带了 message 就用后端的（那里已经是业务语言），
        // 是 axios 自带的「Request failed with status code xxx」就按状态码给一句人话。
        if (isTechnicalMessage(showMsg)) {
            showMsg = friendlyHttpMessage(resp.status);
        }

        // 405 / 404 是"请求打错了地方"，光说"不支持"没法排查。
        // 把实际的方法和路径带出来 —— 之前排查 editor 的 405 就是卡在这，
        // 前端只弹一句英文，完全看不出是哪个接口。
        if ((resp.status === 405 || resp.status === 404) && url) {
            const cfg = (error && error.config) || {};
            const m = cfg.method ? String(cfg.method).toUpperCase() : '';
            showMsg = showMsg + '（' + (m ? m + ' ' : '') + url + '）';
            if (resp.status === 405) {
                // 最常见的原因：URL 拼出了空段（/survey/question/ → 被规整成 /survey/question，
                // 撞上 PUT/POST 映射）。控制台留一份，方便直接定位调用方。
                console.error('[HTTP 405] 方法不被支持：' + m + ' ' + url
                    + ' —— 通常意味着 URL 里某个 id 是空的')
            }
        }

        // 组件里普遍写的是 `(error.message || '未知错误')`，
        // 所以这里直接把 error.message 换成人话 —— 一处改动，
        // 20 多个题型的「保存失败: xxx」提示全部跟着变可读。
        // 原始技术信息保留在 technicalMessage，排障时用它。
        error.technicalMessage = error.message;
        error.statusCode = resp.status;
        error.friendlyMessage = showMsg;
        error.message = showMsg;

        // 只有当请求配置中没有设置 noMessage 为 true 时，才显示全局错误提示
        if (!error.config || !error.config.noMessage) {
            Message({
                message: showMsg,
                type: 'error',
            });
        }
    }
    return Promise.reject(error)
})

export default service
